/**
 * Redact sensitive query-string values from a URL before it is sent to Sentry.
 *
 * Sentry's built-in URL filtering only looks at the query before `#`, but
 * this app's routes live in the hash (`#/Property/Record?propertyId=…`), so
 * hash queries would otherwise ship verbatim. Both the normal query and the
 * hash query are filtered here.
 *
 * Keys and paths are always kept — only matching VALUES become `[Filtered]` —
 * so events still show which page and which record (`id`, `workorder`,
 * `clientCode`, …) were involved:
 *   /#/Admin/Search?Search=jane%20doe&id=42  →  /#/Admin/Search?Search=[Filtered]&id=42
 *
 * Matching rules (case-insensitive):
 *   - exact keys: `code`, `state` (OAuth/PKCE callback values) and personal
 *     or financial data used as lookup values (Vendor Management `ein`,
 *     `ownerName`, `LastName` resource search; `loanNumber`,
 *     `investorNumber`)
 *   - keys containing a sensitive snippet: free-text search terms, contact
 *     details (`emailAddress`, `phone`) and credentials. Exact matching is
 *     used where a substring match would also hide ids needed for
 *     troubleshooting (`code` → `clientCode`, `loanNumber` → `propertyLoanId`).
 *
 * Order numbers and property addresses are deliberately kept: they identify
 * the record being worked on, like the ids above.
 */

export const FILTERED_VALUE = '[Filtered]';

const EXACT_KEYS = new Set([
  'code',
  'state',
  'ein',
  'ssn',
  'taxid',
  'firstname',
  'lastname',
  'ownername',
  'loannumber',
  'investornumber',
]);
const KEY_SNIPPETS = [
  'search',
  'email',
  'phone',
  'auth',
  'token',
  'secret',
  'password',
  'passwd',
  'session',
  'jwt',
  'bearer',
  'key',
];

function isSensitiveKey(rawKey: string): boolean {
  let key = rawKey;
  try {
    key = decodeURIComponent(rawKey.replace(/\+/g, ' '));
  } catch {
    // Malformed escape — match on the raw key.
  }
  const lower = key.toLowerCase();
  return EXACT_KEYS.has(lower) || KEY_SNIPPETS.some((snippet) => lower.includes(snippet));
}

function scrubQuery(query: string): string {
  return query
    .split('&')
    .map((pair) => {
      const eq = pair.indexOf('=');
      const key = eq === -1 ? pair : pair.slice(0, eq);
      return key && isSensitiveKey(key) ? `${key}=${FILTERED_VALUE}` : pair;
    })
    .join('&');
}

/**
 * Scrub a bare query string (`a=1&b=2`, optionally with a leading `?`), e.g.
 * a span's `url.query` attribute.
 */
export function scrubQueryString(query: string): string {
  return query.startsWith('?') ? `?${scrubQuery(query.slice(1))}` : scrubQuery(query);
}

/** Scrub `path?query` — the part before or after a `#`. */
function scrubPart(part: string): string {
  const q = part.indexOf('?');
  return q === -1 ? part : `${part.slice(0, q + 1)}${scrubQuery(part.slice(q + 1))}`;
}

const URL_LIKE = /^(https?:\/\/|\/|#)/;

/**
 * Scrub every URL-looking string (absolute, root-relative or hash) that has a
 * query, anywhere inside a plain object/array. Mutates in place. Used for
 * replay metadata, whose shape varies by event type.
 */
export function scrubUrlsDeep(value: unknown, depth = 0): void {
  if (depth > 6 || value === null || typeof value !== 'object') return;
  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    const v = record[key];
    if (typeof v === 'string') {
      if (v.includes('?') && URL_LIKE.test(v)) record[key] = scrubUrl(v);
    } else if (v && typeof v === 'object') {
      scrubUrlsDeep(v, depth + 1);
    }
  }
}

/**
 * Scrub URL tokens inside free text, e.g. an HTTP span name
 * ("GET https://api/x?Search=…"). Non-URL words are left untouched.
 */
export function scrubUrlsInText(text: string): string {
  return text
    .split(' ')
    .map((word) => (word.includes('?') && URL_LIKE.test(word) ? scrubUrl(word) : word))
    .join(' ');
}

export function scrubUrl(url: string): string;
export function scrubUrl(url: string | undefined): string | undefined;
export function scrubUrl(url: string | undefined): string | undefined {
  if (!url) return url;
  const hash = url.indexOf('#');
  if (hash === -1) return scrubPart(url);
  return `${scrubPart(url.slice(0, hash))}#${scrubPart(url.slice(hash + 1))}`;
}
